import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, throwError, lastValueFrom } from "rxjs";
import { catchError, take } from "rxjs/operators";
import axios from "axios";
import { environment } from "../../../environments/environment";

export interface CustomHttpResult {
  message: string;
  result: any;
  statusCode: number;
}

@Injectable({
  providedIn: "root",
})
export class HttpService {
  private apiUrl = environment.apiUrl;
  private apiTimeout = environment.apiTimeout;

  constructor(private http: HttpClient) {}

  private getHeaders(authorization?: boolean): HttpHeaders {
    const headersObj: { [key: string]: string } = {
      "Content-Type": "application/json",
      accept: "application/json",
    };

    if (authorization) {
      const token = this.getAuthToken();
      if (token) {
        headersObj["Authorization"] = `Bearer ${token}`;
        console.log(
          "Authorization 헤더 추가됨 - 토큰:",
          token.substring(0, 20) + "..."
        );
      } else {
        console.log("토큰이 없어서 Authorization 헤더 추가 안됨");
      }
    }

    console.log("헤더 객체:", headersObj);
    return new HttpHeaders(headersObj);
  }

  private getAuthToken(): string | null {
    return localStorage.getItem(environment.auth.tokenKey);
  }

  private handleError(error: any): Observable<never> {
    // 서버에서 에러 응답이 있을 경우
    if (error.error instanceof ErrorEvent) {
      // 클라이언트 측이나 네트워크 문제로 발생한 에러
      console.error(
        "A client-side or network error occurred:",
        error.error.message
      );
      return throwError(`Client-side error: ${error.error.message}`);
    } else {
      // 서버 측에서 반환한 에러 응답을 처리
      if (error.status === 0) {
        // 네트워크 오류 등
        return throwError(
          "서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요."
        );
      }

      // 서버에서 반환된 에러 메시지 사용
      const errorMessage =
        error.error?.message || "알 수 없는 서버 오류가 발생했습니다.";

      // 상태 코드와 에러 메시지 출력
      console.error(
        `Backend returned code ${error.status}, body was: ${errorMessage}`
      );

      // 에러 메시지 반환
      return throwError(errorMessage);
    }
  }

  doGet(path: string, authorization?: boolean, params?: any) {
    const headers = this.getHeaders(authorization);
    const options = { headers, params };
    return lastValueFrom(
      this.http
        .get<CustomHttpResult>(`${this.apiUrl}${path}`, options)
        .pipe(catchError(this.handleError))
        .pipe(take(1))
    );
  }

  doPost(path: string, data?: any, authorization?: boolean, params?: any) {
    const headers = this.getHeaders(authorization);
    const options = { headers, params };
    return lastValueFrom(
      this.http
        .post<CustomHttpResult>(`${this.apiUrl}${path}`, data, options)
        .pipe(catchError(this.handleError))
        .pipe(take(1))
    );
  }

  doPatch(path: string, data?: any, authorization?: boolean) {
    const headers = this.getHeaders(authorization);
    return lastValueFrom(
      this.http
        .patch<CustomHttpResult>(`${this.apiUrl}${path}`, data, { headers })
        .pipe(catchError(this.handleError))
        .pipe(take(1))
    );
  }

  doPut(path: string, data?: any, authorization?: boolean, params?: any) {
    const headers = this.getHeaders(authorization);
    console.log("doPut 헤더:", headers);

    // 헤더 내용을 직접 확인
    const headerKeys = headers.keys();
    console.log("doPut 헤더 키들:", headerKeys);
    headerKeys.forEach((key) => {
      const value = headers.get(key);
      console.log(`doPut 헤더 ${key}:`, value);
    });

    const options = { headers, params };
    return lastValueFrom(
      this.http
        .put<CustomHttpResult>(`${this.apiUrl}${path}`, data, options)
        .pipe(catchError(this.handleError))
        .pipe(take(1))
    );
  }

  doDelete(path: string, authorization?: boolean, params?: any, data?: any) {
    const headers = this.getHeaders(authorization);
    const options = { headers, params, body: data };

    return lastValueFrom(
      this.http
        .delete<CustomHttpResult | any>(`${this.apiUrl}${path}`, options)
        .pipe(catchError(this.handleError))
        .pipe(take(1))
    );
  }

  async doFilePost(path: string, data?: any): Promise<any> {
    const token = this.getAuthToken();
    const headers: any = {
      "Content-Type": "multipart/form-data",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const result: any = await axios.post(this.apiUrl + path, data, { headers });

    console.log("doFilePost 응답:", result.data);

    if (result?.status === 201) {
      // result.data.result[0].Location 형태로 반환되는 경우
      if (
        result.data.result &&
        result.data.result[0] &&
        result.data.result[0].Location
      ) {
        return result.data.result[0].Location;
      }
      // result.data.result가 배열인 경우
      if (Array.isArray(result.data.result)) {
        return result.data.result[0];
      }
      // result.data가 배열인 경우
      if (Array.isArray(result.data)) {
        return result.data[0];
      }
      // 그 외의 경우
      return result.data;
    } else {
      return result.data;
    }
  }
}
